import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/v2/components';

import { IntegrationItem } from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { useIntegrationService } from '@/src/infrastructure/Protocol/Integrations/useIntegrationService';

export const useActionsIntegrations = () => {
  const service = useIntegrationService();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const deleteAction = useCallback(
    async (ids: number[]) => {
      if (!service) return;
      setIsLoading(true);
      const response = await service.deleteIntegrations(ids);
      if (response.success) {
        toast({
          variant: 'success',
          title: t('useActionsJourneys.deleteAction.title.success'),
          body: t('useActionsIntegrations.deleteAction.body.success'),
        });
      } else {
        toast({
          variant: 'error',
          title: t('useActionsJourneys.deleteAction.title.error'),
          body: t('useActionsJourneys.deleteAction.body.error'),
        });
      }
      setIsLoading(false);
    },
    [service, t]
  );

  const createIntegration = useCallback(
    async (
      name: string
    ): Promise<
      | { isValidInput: true; data: IntegrationItem }
      | { isValidInput: false; message: string }
      | undefined
    > => {
      if (!service) return;

      setIsLoading(true);
      const response = await service.createIntegration(name);
      if (response.success) {
        toast({
          id: 'integrationCreated',
          title: t(`useActionsIntegrations.success.title`),
          body: t(`useActionsIntegrations.success.body`),
        });
        setIsLoading(false);
        return { isValidInput: true, data: response.data as IntegrationItem };
      } else if (response.data === 'JOURNEY_ALREADY_EXISTS') {
        setIsLoading(false);
        return {
          isValidInput: false,
          message: t(`useActionsIntegrations.error.JOURNEY_ALREADY_EXISTS`),
        };
      }

      toast({
        id: 'integrationCreated',
        title: t(`useActionsIntegrations.error.title`),
        body: t(`useActionsIntegrations.error.body`),
        variant: 'error',
      });
      setIsLoading(false);
      return {
        isValidInput: false,
        message: t(`useActionsIntegrations.error.body`),
      };
    },
    [service, t]
  );

  const duplicateIntegration = useCallback(
    async (name: string, id: number) => {
      if (!service) return;
      setIsLoading(true);
      const response = await service.duplicateIntegration(name, id);
      if (response.success) {
        toast({
          variant: 'success',
          title: t('useActionsJourneys.duplicateMessage.title.success'),
          body: t('useActionsIntegrations.duplicateMessage.body.success'),
        });
        setIsLoading(false);
        return { isValidInput: response.success, message: response.data };
      } else {
        setIsLoading(false);
        return {
          isValidInput: response.success,
          message: t(`useActionsIntegrations.duplicateMessage.body.error.RULE_ALREADY_EXISTS`),
        };
      }
    },
    [service, t]
  );

  const renameIntegration = useCallback(
    async (name: string, id: number) => {
      if (!service) return;
      setIsLoading(true);
      const response = await service.renameIntegration(name, id);
      if (response.success) {
        toast({
          variant: 'success',
          title: t('useActionsJourneys.renameMessage.title.success'),
          body: t('useActionsJourneys.renameMessage.body.success'),
        });
        setIsLoading(false);
        return { isValidInput: response.success, message: response.data };
      } else {
        setIsLoading(false);
        return {
          isValidInput: response.success,
          message: t(`useActionsIntegrations.duplicateMessage.body.error.RULE_ALREADY_EXISTS`),
        };
      }
    },
    [service, t]
  );

  return { deleteAction, isLoading, createIntegration, duplicateIntegration, renameIntegration };
};
